import { useMemo } from "react";
import { toast } from "react-toastify";

import { THEME_COLOR } from "constants/style.constants";
import _debounce from "lodash/debounce";

import { ColorPicker } from "components/base";
import { ShapefileSourceType } from "components/project/layers/hooks/project-layer-tree/useProjectLayerTree.types";
import { useProjectContext } from "components/project/projects/context";
import { useSelectedProjectPermissions } from "components/project/projects/hooks";
import { useShapefileDispatch } from "components/project/shapefiles/context";
import { useUpdateProjectLinkedShapefileFolderColourMutation } from "components/project/shapefiles/mutations";
import {
  INVALID_LINE_STYLE_MESSAGE,
  ShapefileStyleProp,
  type ShapefileStylePropT,
  isLineStyle
} from "components/project/shapefiles/shapefiles.constants";

export interface IProjectLinkedShapefileFolderColorPickerProps {
  shapefileNodeId: string;
  type: ShapefileSourceType;
  lineStyle?: string;
  fillPattern?: string;
  place?: "left" | "right" | "top" | "bottom";
}

export function ProjectLinkedShapefileFolderColorPicker(
  props: IProjectLinkedShapefileFolderColorPickerProps
) {
  const { selectedProjectId } = useProjectContext();
  const shapefileDispatch = useShapefileDispatch();
  const { type, place } = props;

  const projectPermissions = useSelectedProjectPermissions();
  const updateProjectLinkedShapefileFolderColour =
    useUpdateProjectLinkedShapefileFolderColourMutation({});

  const handleChange = (key: ShapefileStylePropT, value: string) => {
    if (key === ShapefileStyleProp.LineStyle && !isLineStyle(value)) {
      toast.error(INVALID_LINE_STYLE_MESSAGE);
      return;
    }
    updateProjectLinkedShapefileFolderColour.mutate({
      body: { key, value },
      projectId: selectedProjectId,
      shapefileNodeId: props.shapefileNodeId,
      type
    });
  };

  const debouncedUpdateOpacity = useMemo(
    () =>
      _debounce(
        (value) =>
          updateProjectLinkedShapefileFolderColour.mutate({
            body: {
              key: "opacity",
              value
            },
            projectId: selectedProjectId,
            shapefileNodeId: props.shapefileNodeId,
            type
          }),
        500
      ),
    [selectedProjectId]
  );

  return (
    <div className="shapefile-color-picker-action">
      <ColorPicker
        isAtFolderLevel={true}
        place={place}
        onChange={(key, value) => {
          handleChange(key, value);
          shapefileDispatch({ payload: { isLoadingColors: true } });
        }}
        onChangeOpacity={(key, value) => debouncedUpdateOpacity(value)}
        onChangeLineStyle={(key, value) => handleChange(key, value)}
        onChangeFillPattern={(key, value) => handleChange(key, value)}
        values={{
          color: THEME_COLOR,
          strokeColor: THEME_COLOR,
          thickness: 2,
          lineStyle: props.lineStyle ?? "solid",
          fillPattern: props.fillPattern ?? "none",
          opacity: 100
        }}
        isReadOnly={!projectPermissions.canEditShapefiles}
      />
    </div>
  );
}
